import { useCallback } from 'react';
import { nanoid } from 'nanoid';
import { useReactFlow, getIncomers } from '@xyflow/react';
import { useNodeOperations } from '@/providers/node-operations';
import { handleError } from '@/lib/error/handle';
import { computeUpstreamGridPosition } from '@/lib/nodes/upstream-grid-position';

interface UseVideoGuidanceActionsParams {
  id: string;
  t: (key: string) => string;
}

export function useVideoGuidanceActions({ id, t }: UseVideoGuidanceActionsParams) {
  const { getNode, addEdges, getNodes, getEdges } = useReactFlow();
  const { addNode } = useNodeOperations();

  // 快捷操作：添加上传节点并连接（图片/视频共用网格定位）
  const addUploadNode = useCallback(
    (fileType: 'image' | 'video', edgeType?: string) => {
      const currentNode = getNode(id);
      if (!currentNode) return;

      const incomers = getIncomers({ id }, getNodes(), getEdges());
      const position = computeUpstreamGridPosition(currentNode, incomers);

      const newNodeId = addNode('upload', { position, data: { fileType } });

      // 连接新节点到当前视频节点（使用 requestAnimationFrame 确保节点已创建）
      requestAnimationFrame(() => {
        addEdges({
          id: nanoid(),
          source: newNodeId,
          target: id,
          ...(edgeType ? { type: edgeType } : {}),
        });
      });
    },
    [id, getNode, addNode, addEdges, getNodes, getEdges],
  );

  const handleAddImageNode = useCallback(() => {
    try {
      addUploadNode('image', 'animated');
    } catch (error) {
      handleError(t('Failed to add image upload node'), error);
    }
  }, [addUploadNode, t]);

  const handleAddVideoNode = useCallback(() => {
    try {
      addUploadNode('video');
    } catch (error) {
      handleError(t('Failed to add video upload node'), error);
    }
  }, [addUploadNode, t]);

  return {
    handleAddImageNode,
    handleAddVideoNode,
  };
}
